import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { axiosInstance } from '../utils/axiosConfig';
import './ConsentRenewalPrompt.css';

interface RenewalData {
  id: string;
  type: string;
  renewalDueAt: string;
  grantedAt: string;
}

interface RenewalResponse {
  renewalsDue: RenewalData[];
  count: number;
}

const ConsentRenewalPrompt: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [renewalsDue, setRenewalsDue] = useState<RenewalData[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) return;

    const checkRenewals = async () => {
      try {
        const response = await axiosInstance.get('/consents/renewals-due');

        if (response.status === 200) {
          const data: RenewalResponse = response.data;
          setRenewalsDue(data.renewalsDue);
          if (data.count > 0) {
            setShowModal(true);
          }
        }
      } catch (error) {
        console.error('Failed to check consent renewals:', error);
      } finally {
        setLoading(false);
      }
    };

    checkRenewals();
  }, [isAuthenticated]);

  const handleRenewConsent = async (consentType: string) => {
    try {
      const mapTypeToEndpoint: Record<string, string> = {
        'data-storage': 'data-storage',
        'ai-review': 'ai-review',
        'email-access': 'email-access',
        'web-crawl': 'web-crawl',
      };

      const endpoint = mapTypeToEndpoint[consentType] || consentType;
      await axiosInstance.post(`/consents/${endpoint}`, { agreed: true });

      setRenewalsDue(renewalsDue.filter(r => r.type !== consentType));
      if (renewalsDue.length === 1) {
        setShowModal(false);
      }
    } catch (error) {
      console.error('Failed to renew consent:', error);
    }
  };

  const handleDismiss = () => {
    setShowModal(false);
  };

  if (loading || !showModal || renewalsDue.length === 0) {
    return null;
  }

  const typeLabels: Record<string, string> = {
    data_storage: 'Data Storage',
    email_access: 'Email Access',
    ai_review: 'AI Review',
    web_crawl: 'Web Crawl',
  };

  return (
    <div className="consent-renewal-modal-overlay">
      <div className="consent-renewal-modal">
        <div className="renewal-header">
          <h2>Consent Renewal Required</h2>
          <p>Your consent{renewalsDue.length > 1 ? 's' : ''} {renewalsDue.length > 1 ? 'have' : 'has'} expired and need renewal.</p>
        </div>

        <div className="renewal-list">
          {renewalsDue.map(renewal => (
            <div key={renewal.id} className="renewal-item">
              <div className="renewal-info">
                <h3>{typeLabels[renewal.type] || renewal.type}</h3>
                <p className="renewal-date">
                  Granted: {new Date(renewal.grantedAt).toLocaleDateString()}
                </p>
              </div>
              <button
                className="btn-renew"
                onClick={() => handleRenewConsent(renewal.type)}
              >
                Renew
              </button>
            </div>
          ))}
        </div>

        <div className="renewal-actions">
          <button className="btn-dismiss" onClick={handleDismiss}>
            Dismiss for Now
          </button>
          <p className="renewal-note">
            You can renew these consents anytime from your Settings page.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ConsentRenewalPrompt;
